import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, Clock, ChevronRight, ChevronDown, ChevronUp,
  CheckCircle, RotateCcw, X, Zap, ThumbsUp, ThumbsDown,
  ArrowRight, Loader2, GraduationCap
} from 'lucide-react';
import api from '../utils/api';

/**
 * GrammarSRSWidget — Dashboard grammar review widget.
 *
 * Two modes:
 * 1. **List mode** (default): shows due grammar topics with urgency badges.
 * 2. **Review mode**: card-by-card flashcard flow with content + exercises,
 *    then Hard / Good / Easy quality rating that calls the SRS backend.
 */

const GrammarSRSWidget = ({ className = '' }) => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalDue, setTotalDue] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(0);

  // Review mode state
  const [reviewMode, setReviewMode] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [topicContent, setTopicContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sessionStats, setSessionStats] = useState({ reviewed: 0, hard: 0, good: 0, easy: 0 });

  const languageInfo = {
    en: { name: 'English', flag: 'gb' },
    es: { name: 'Spanish', flag: 'es' },
    fr: { name: 'French', flag: 'fr' },
    de: { name: 'German', flag: 'de' },
    fa: { name: 'Persian', flag: 'ir' },
    ar: { name: 'Arabic', flag: 'sa' },
    ru: { name: 'Russian', flag: 'ru' },
    zh: { name: 'Chinese', flag: 'cn' },
    ja: { name: 'Japanese', flag: 'jp' },
    ko: { name: 'Korean', flag: 'kr' },
    pt: { name: 'Portuguese', flag: 'pt' },
    it: { name: 'Italian', flag: 'it' },
  };

  const statusColors = {
    due_today: { bg: 'bg-amber-500/20', border: 'border-amber-500/30', text: 'text-amber-400' },
    overdue: { bg: 'bg-orange-500/20', border: 'border-orange-500/30', text: 'text-orange-400' },
    critical: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400' },
    upcoming: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'text-yellow-400' },
    scheduled: { bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-400' },
  };

  useEffect(() => {
    fetchTodaysReviews();
  }, []);

  const fetchTodaysReviews = async () => {
    try {
      setLoading(true);
      const response = await api.get('/grammar-library/srs/today?limit=20');
      setReviews(response.data.reviews || []);
      setTotalDue(response.data.total_due || 0);
      setEstimatedTime(response.data.estimated_time || 0);
    } catch (err) {
      console.error('Failed to fetch SRS reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load full topic content for review mode
  const loadTopicContent = useCallback(async (topicId) => {
    setLoadingContent(true);
    setShowAnswer(false);
    setTopicContent(null);
    try {
      const response = await api.get(`/grammar-library/topic/${topicId}`);
      setTopicContent(response.data);
    } catch (err) {
      console.error('Failed to load topic:', err);
      setTopicContent(null);
    } finally {
      setLoadingContent(false);
    }
  }, []);

  const startReview = () => {
    if (reviews.length === 0) return;
    setReviewMode(true);
    setCurrentIndex(0);
    setSessionStats({ reviewed: 0, hard: 0, good: 0, easy: 0 });
    loadTopicContent(reviews[0].id);
  };

  const submitRating = async (quality) => {
    const current = reviews[currentIndex];
    if (!current) return;
    setSubmitting(true);
    try {
      await api.post(`/grammar-library/srs/complete/${current.id}?quality=${quality}`);
      const label = quality <= 2 ? 'hard' : quality >= 4 ? 'easy' : 'good';
      setSessionStats(prev => ({
        ...prev,
        reviewed: prev.reviewed + 1,
        [label]: prev[label] + 1,
      }));

      // Advance to next card
      const nextIdx = currentIndex + 1;
      if (nextIdx < reviews.length) {
        setCurrentIndex(nextIdx);
        loadTopicContent(reviews[nextIdx].id);
      } else {
        // Session complete
        setReviewMode(false);
        fetchTodaysReviews(); // refresh list
      }
    } catch (err) {
      console.error('Failed to submit rating:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const exitReview = () => {
    setReviewMode(false);
    fetchTodaysReviews();
  };

  const FlagImage = ({ langCode, size = 20 }) => {
    const info = languageInfo[langCode];
    const countryCode = info?.flag || 'un';
    return (
      <img
        src={`https://flagcdn.com/w40/${countryCode}.png`}
        alt={info?.name || langCode}
        className="rounded-sm object-cover"
        style={{ width: size, height: size * 0.75 }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  };

  // ──────────── Loading state ────────────
  if (loading) {
    return (
      <div className={`bg-slate-800/50 border border-slate-700 rounded-xl p-4 ${className}`}>
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500" />
        </div>
      </div>
    );
  }

  // ──────────── Session complete summary ────────────
  if (!reviewMode && sessionStats.reviewed > 0) {
    return (
      <div className={`bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-4 ${className}`} data-testid="grammar-review-done">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-white font-medium">Review Session Complete</span>
        </div>
        <div className="flex items-center gap-4 text-sm mb-3">
          <span className="text-green-400">{sessionStats.reviewed} reviewed</span>
          {sessionStats.hard > 0 && <span className="text-red-400">{sessionStats.hard} hard</span>}
          {sessionStats.good > 0 && <span className="text-amber-400">{sessionStats.good} good</span>}
          {sessionStats.easy > 0 && <span className="text-emerald-400">{sessionStats.easy} easy</span>}
        </div>
        {totalDue > 0 ? (
          <button
            onClick={startReview}
            className="w-full text-center text-sm text-amber-400 hover:text-amber-300 py-2 bg-amber-500/10 rounded-lg border border-amber-500/20"
            data-testid="grammar-review-more-btn"
          >
            {totalDue} more due — continue reviewing
          </button>
        ) : (
          <p className="text-green-400 text-sm">All caught up! No more reviews due today.</p>
        )}
        <button
          onClick={() => { setSessionStats({ reviewed: 0, hard: 0, good: 0, easy: 0 }); }}
          className="mt-2 text-xs text-slate-500 hover:text-slate-300"
        >
          Dismiss
        </button>
      </div>
    );
  }

  // ──────────── No reviews due ────────────
  if (!reviewMode && reviews.length === 0) {
    return (
      <div className={`bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-white font-medium">Grammar Reviews</span>
        </div>
        <p className="text-green-400 text-sm">All caught up! No reviews due today.</p>
        <button
          onClick={() => navigate('/grammar-library')}
          className="mt-3 text-xs text-slate-400 hover:text-white flex items-center gap-1"
        >
          <BookOpen className="w-3 h-3" />
          Learn new topics
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // ──────────── Review Mode — card-by-card ────────────
  if (reviewMode) {
    const current = reviews[currentIndex];
    const progress = reviews.length > 0 ? ((currentIndex) / reviews.length) * 100 : 0;

    return (
      <div className={`bg-slate-800/60 border border-slate-700 rounded-xl overflow-hidden ${className}`} data-testid="grammar-review-mode">
        {/* Review header bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-900/60 border-b border-slate-700">
          <div className="flex items-center gap-2 text-sm">
            <GraduationCap className="w-4 h-4 text-purple-400" />
            <span className="text-white font-medium">Grammar Review</span>
            <span className="text-slate-400">{currentIndex + 1}/{reviews.length}</span>
          </div>
          <button onClick={exitReview} className="p-1 text-slate-400 hover:text-white" data-testid="grammar-review-exit">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-slate-700">
          <div className="h-full bg-purple-500 transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>

        {/* Card content */}
        <div className="p-4">
          {loadingContent ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
            </div>
          ) : topicContent ? (
            <div className="space-y-3">
              {/* Topic header */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {topicContent.language && <FlagImage langCode={topicContent.language} size={20} />}
                  {topicContent.cefr_level && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                      {topicContent.cefr_level}
                    </span>
                  )}
                  {current?.memory_strength !== undefined && (
                    <span className="text-xs text-slate-500 ml-auto">
                      {Math.round(current.memory_strength * 100)}% memory
                    </span>
                  )}
                </div>
                <h3 className="text-white font-semibold text-lg">
                  {topicContent.title || 'Grammar Topic'}
                </h3>
                {topicContent.objectives && (
                  <p className="text-slate-400 text-sm mt-1">{topicContent.objectives}</p>
                )}
              </div>

              {/* Reveal / Content area */}
              {!showAnswer ? (
                <div className="space-y-3">
                  <p className="text-slate-400 text-sm italic">
                    Try to recall the rules and patterns for this topic before revealing the content.
                  </p>
                  <button
                    onClick={() => setShowAnswer(true)}
                    className="w-full py-3 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/30 rounded-lg text-purple-300 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    data-testid="grammar-review-reveal"
                  >
                    <ChevronDown className="w-4 h-4" />
                    Show Content
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Grammar content */}
                  {topicContent.content && (
                    <div className="bg-slate-900/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                        {topicContent.content}
                      </p>
                    </div>
                  )}

                  {/* Exercises preview */}
                  {topicContent.exercises?.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">Exercises</span>
                      {topicContent.exercises.slice(0, 3).map((ex, idx) => (
                        <div key={idx} className="bg-slate-900/40 rounded-lg p-2.5">
                          <p className="text-white text-sm">{ex.question || ex}</p>
                          {ex.answer && (
                            <details className="mt-1.5">
                              <summary className="text-xs text-purple-400 cursor-pointer hover:text-purple-300">
                                Show answer
                              </summary>
                              <p className="text-slate-400 text-sm mt-1 pl-2 border-l-2 border-purple-500/30">
                                {ex.answer}
                              </p>
                            </details>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Rating buttons */}
                  <div className="pt-2 border-t border-slate-700">
                    <p className="text-xs text-slate-500 text-center mb-2">How well did you remember this?</p>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => submitRating(1)}
                        disabled={submitting}
                        className="py-2.5 bg-red-500/20 hover:bg-red-500/40 border border-red-500/30 rounded-lg text-red-400 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                        data-testid="grammar-rate-hard"
                      >
                        <ThumbsDown className="w-3.5 h-3.5" />
                        Hard
                      </button>
                      <button
                        onClick={() => submitRating(3)}
                        disabled={submitting}
                        className="py-2.5 bg-amber-500/20 hover:bg-amber-500/40 border border-amber-500/30 rounded-lg text-amber-400 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                        data-testid="grammar-rate-good"
                      >
                        <ThumbsUp className="w-3.5 h-3.5" />
                        Good
                      </button>
                      <button
                        onClick={() => submitRating(5)}
                        disabled={submitting}
                        className="py-2.5 bg-emerald-500/20 hover:bg-emerald-500/40 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                        data-testid="grammar-rate-easy"
                      >
                        <Zap className="w-3.5 h-3.5" />
                        Easy
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-4">Failed to load topic content</p>
          )}
        </div>
      </div>
    );
  }

  // ──────────── List mode — due topics with "Start Review" ────────────
  return (
    <div className={`bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4 ${className}`} data-testid="grammar-review-list">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-amber-500/20 rounded-lg">
            <RotateCcw className="w-4 h-4 text-amber-400" />
          </div>
          <span className="text-white font-medium text-sm">Grammar Reviews</span>
          {totalDue > 0 && (
            <span className="px-1.5 py-0.5 bg-amber-500/30 text-amber-400 text-xs rounded-full">
              {totalDue}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <Clock className="w-3 h-3" />
          ~{estimatedTime}m
        </div>
      </div>

      {/* Start Review Button */}
      <button
        onClick={startReview}
        className="w-full mb-3 py-2.5 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/30 rounded-lg text-purple-300 text-sm font-medium transition-colors flex items-center justify-center gap-2"
        data-testid="grammar-start-review-btn"
      >
        <GraduationCap className="w-4 h-4" />
        Start Review ({reviews.length} topic{reviews.length !== 1 ? 's' : ''})
      </button>

      {/* Review Cards preview */}
      <div className="space-y-2">
        {reviews.slice(0, 5).map((review) => {
          const colors = statusColors[review.srs_status?.status] || statusColors.due_today;
          return (
            <button
              key={review.id}
              onClick={() => navigate(`/grammar-library?open=${review.id}`)}
              className={`w-full p-2.5 rounded-lg ${colors.bg} border ${colors.border} hover:bg-opacity-80 transition-all text-left`}
            >
              <div className="flex items-center gap-2">
                <FlagImage langCode={review.language_code} size={18} />
                <div className="flex-1 min-w-0">
                  <h4 className="text-white text-sm font-medium truncate">
                    {review.title || review.objectives?.[0]?.slice(0, 50) || 'Grammar Review'}
                  </h4>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs ${colors.text}`}>
                      {review.srs_status?.message}
                    </span>
                    {review.memory_strength !== undefined && (
                      <span className="text-xs text-slate-500">
                        · {Math.round(review.memory_strength * 100)}% memory
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 ${colors.text} flex-shrink-0`} />
              </div>
            </button>
          );
        })}
      </div>

      {/* View All */}
      {totalDue > 5 && (
        <button
          onClick={() => navigate('/grammar-library')}
          className="w-full mt-3 text-center text-xs text-amber-400 hover:text-amber-300 flex items-center justify-center gap-1"
        >
          View all {totalDue} reviews
          <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default GrammarSRSWidget;
